import { useState } from 'react';
import { Phone, MapPin, Clock, Mail, Send, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_7a187c08-1566-4a5d-94a6-902036d37fa8/artifacts/oin9oc2t_Untitled%20design%20%2814%29.png";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: '',
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    // Try backend API first (with short timeout so Netlify fallback is fast)
    try {
      const res = await axios.post(`${API}/contact`, formData, { timeout: 5000 });
      setStatus({ type: 'success', message: res.data.message });
      setFormData({ name: '', email: '', phone: '', service_type: '', message: '' });
      setLoading(false);
      return;
    } catch (apiErr) {
      console.log('API unavailable, using Netlify Forms...');
    }

    // Netlify Forms fallback (works on Netlify deploy)
    try {
      const formBody = new URLSearchParams({
        'form-name': 'contact',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.service_type,
        message: formData.message,
      });
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (res.ok) {
        setStatus({ type: 'success', message: "Thank you for contacting us! We'll get back to you shortly." });
        setFormData({ name: '', email: '', phone: '', service_type: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (netlifyErr) {
      setStatus({ type: 'error', message: 'Something went wrong. Please call us at (870) 425-4295 or email polyclean2024@gmail.com.' });
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-poly-bg relative overflow-hidden" data-testid="contact-section">
      {/* Logo watermark */}
      <img src={LOGO_URL} alt="" className="logo-watermark absolute -right-20 top-10 w-80 h-80" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="bg-white rounded-full p-1 shadow-md ring-1 ring-slate-100">
              <img src={LOGO_URL} alt="Poly Clean Center" className="w-8 h-8 object-contain" />
            </div>
            <span className="text-poly-blue-light font-semibold text-sm tracking-wider uppercase">Get In Touch</span>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-poly-blue-dark tracking-tight mb-4">
            Contact <span className="text-poly-blue">Poly Clean Center</span>
          </h2>
          <p className="text-poly-muted text-base sm:text-lg">
            Ready to schedule a pickup, request a quote, or have a question? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left: Form */}
          <div className="lg:col-span-3 reveal animate-fade-in-left">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 shadow-sm">
              <h3 className="font-heading font-bold text-xl text-poly-blue-dark mb-6">Send Us a Message</h3>
              
              {status && (
                <div
                  className={`mb-6 p-4 rounded-xl flex items-start gap-3 animate-fade-in-up ${
                    status.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                  data-testid="form-status"
                >
                  {status.type === 'success' && <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />}
                  <span className="text-sm">{status.message}</span>
                </div>
              )}

              {/* Netlify Forms: hidden form-name for detection */}
              <form
                onSubmit={handleSubmit}
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                className="space-y-5"
                data-testid="contact-form"
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>Don't fill this out: <input name="bot-field" /></label>
                </p>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-poly-blue-dark font-medium text-sm mb-2 block">Full Name *</Label>
                    <Input
                      id="name" name="name" value={formData.name} onChange={handleChange} required
                      placeholder="John Smith"
                      className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-poly-blue-light/20 transition-all"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-poly-blue-dark font-medium text-sm mb-2 block">Email Address *</Label>
                    <Input
                      id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                      placeholder="john@example.com"
                      className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-poly-blue-light/20 transition-all"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-poly-blue-dark font-medium text-sm mb-2 block">Phone Number</Label>
                    <Input
                      id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange}
                      placeholder="(870) 555-0000"
                      className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-poly-blue-light/20 transition-all"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_type" className="text-poly-blue-dark font-medium text-sm mb-2 block">Service Needed</Label>
                    <select
                      id="service_type" name="service_type" value={formData.service_type} onChange={handleChange}
                      className="flex h-12 w-full rounded-xl border border-slate-200 bg-transparent px-3 py-1 text-base shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-poly-blue-light/20 text-poly-blue-dark"
                      data-testid="contact-service-select"
                    >
                      <option value="">Select a service...</option>
                      <option value="pickup-delivery">Pickup & Delivery</option>
                      <option value="wash-dry-fold">Wash, Dry & Fold</option>
                      <option value="commercial">Commercial Laundry</option>
                      <option value="self-service">Self-Service Info</option>
                      <option value="ironing">Ironing & Pressing</option>
                      <option value="alterations">Alterations</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-poly-blue-dark font-medium text-sm mb-2 block">Message *</Label>
                  <Textarea
                    id="message" name="message" value={formData.message} onChange={handleChange} required
                    placeholder="Tell us how we can help..."
                    rows={4}
                    className="rounded-xl border-slate-200 focus:ring-2 focus:ring-poly-blue-light/20 transition-all"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-poly-orange hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold px-8 py-3.5 rounded-full transition-all shadow-lg shadow-orange-500/25 hover:scale-105 active:scale-95"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
              </form>
            </div>
          </div>

          {/* Right: Info + Map */}
          <div className="lg:col-span-2 space-y-6 reveal reveal-delay-2">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-white rounded-full p-1 shadow-md ring-1 ring-slate-100">
                  <img src={LOGO_URL} alt="Poly Clean Center" className="w-12 h-12 object-contain" loading="lazy" />
                </div>
                <div>
                  <span className="font-heading font-bold text-lg text-poly-blue-dark block">Poly Clean Center</span>
                  <span className="text-poly-muted text-xs">Laundromat</span>
                </div>
              </div>

              <div className="space-y-4">
                <a href="tel:8704254295" className="flex items-start gap-3 group" data-testid="contact-info-phone">
                  <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-poly-blue" />
                  </div>
                  <div>
                    <span className="text-poly-blue-dark font-semibold text-sm block group-hover:text-poly-blue transition-colors">(870) 425-4295</span>
                    <span className="text-poly-muted text-xs">Call for service questions or quotes</span>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-poly-orange" />
                  </div>
                  <div>
                    <span className="text-poly-blue-dark font-semibold text-sm block">715 South Street</span>
                    <span className="text-poly-muted text-xs">Mountain Home, AR 72653</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-poly-green" />
                  </div>
                  <div>
                    <span className="text-poly-blue-dark font-semibold text-sm block">Business Hours</span>
                    <span className="text-poly-muted text-xs block">Mon - Fri: 8:00 AM - 5:00 PM</span>
                    <span className="text-poly-muted text-xs block">Saturday: 8:00 AM - 4:00 PM</span>
                    <span className="text-poly-muted text-xs block">Sunday: Closed</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-poly-blue" />
                  </div>
                  <div>
                    <a href="mailto:polyclean2024@gmail.com" className="text-poly-blue-dark font-semibold text-sm block hover:text-poly-blue transition-colors">polyclean2024@gmail.com</a>
                    <span className="text-poly-muted text-xs">Email us anytime</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-800 text-xs font-medium">Note: Our phone line does not receive text messages. Please call or email us instead.</p>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm h-64" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3228.5!2d-92.3855!3d36.3345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87d1a8e0c1a7e5e3%3A0x1234567890abcdef!2s715%20South%20St%2C%20Mountain%20Home%2C%20AR%2072653!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Poly Clean Center Laundromat Location"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
